/**
 * Shared Progress Card — Overview card showing shared progress
 *
 * Adapts to privacy style. Shows shared goals, resets, milestones.
 */
import { usePrivacy } from "./PrivacyProvider";
import { Target, RefreshCw, TrendingUp } from "lucide-react";

const MILESTONE_MESSAGES = {
  25: "Making steady progress",
  50: "Halfway there",
  75: "This is taking shape",
  100: "You did it",
};

export function SharedProgressCard() {
  const { sharedProgress, isSolo, together, loading } = usePrivacy();

  if (loading || !sharedProgress) return null;

  const { shared_goals_count, goals_with_progress, resets_this_month, milestones } = sharedProgress;

  // Don't show if nothing to show
  if (shared_goals_count === 0 && resets_this_month === 0) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="shared-progress-card">
      <h3 className="font-heading text-sm font-medium text-foreground mb-3">
        {isSolo ? "Your progress" : "Progress together"}
      </h3>

      <div className="space-y-3">
        {shared_goals_count > 0 && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center shrink-0">
              <Target className="w-4 h-4 text-[#1a4d3e]" />
            </div>
            <div>
              <p className="text-sm text-foreground">
                {shared_goals_count} {isSolo ? "goal" : "shared goal"}{shared_goals_count !== 1 ? "s" : ""} in progress
              </p>
              {goals_with_progress > 0 && (
                <p className="text-xs text-muted-foreground">
                  {goals_with_progress} with progress {together}
                </p>
              )}
            </div>
          </div>
        )}

        {resets_this_month > 0 && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
              <RefreshCw className="w-4 h-4 text-amber-600" />
            </div>
            <p className="text-sm text-foreground">
              {resets_this_month} reset{resets_this_month !== 1 ? "s" : ""} completed this month
            </p>
          </div>
        )}

        {milestones.length > 0 && (
          <div className="border-t border-border pt-3 space-y-2">
            {milestones.slice(0, 3).map((m) => (
              <div key={m.goal_id} className="flex items-center gap-3" data-testid={`milestone-${m.goal_id}`}>
                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-foreground">{m.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.percent}% — {MILESTONE_MESSAGES[m.milestone] || "Making progress"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
